import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import { User, Users, Heart, Clock, MapPin, Phone, Mail, Facebook, Instagram, Twitter, Star } from 'lucide-react';
import { mockData, mockBooking } from '../mock';

const PulseFitStudio = () => {
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [bookingForm, setBookingForm] = useState({ name: '', email: '', phone: '', preferredTime: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [isContactLoading, setIsContactLoading] = useState(false);

  const handleBooking = async (e) => {
    e.preventDefault();
    setIsBookingLoading(true);
    try {
      const result = await mockBooking.bookClass(bookingForm);
      alert(`Success! Your booking ID is: ${result.bookingId}`);
      setBookingForm({ name: '', email: '', phone: '', preferredTime: '' });
    } catch (error) {
      alert('Booking failed. Please try again.');
    }
    setIsBookingLoading(false);
  };

  const handleContact = async (e) => {
    e.preventDefault();
    setIsContactLoading(true);
    try {
      const result = await mockBooking.submitContact(contactForm);
      alert(`Thank you! Your message ID is: ${result.messageId}`);
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      alert('Message failed to send. Please try again.');
    }
    setIsContactLoading(false);
  };

  const handlePlanSelection = async (plan) => {
    try {
      const result = await mockBooking.subscribePlan({ planId: plan.id, planName: plan.name });
      alert(`Welcome to ${plan.name}! Subscription ID: ${result.subscriptionId}`);
    } catch (error) {
      alert('Subscription failed. Please try again.');
    }
  };

  const filteredClasses = selectedFilter === 'All' 
    ? mockData.schedule.classes.Monday 
    : mockData.schedule.classes.Monday.filter(cls => cls.type === selectedFilter);

  const getIcon = (iconName) => {
    const icons = { User, Users, Heart };
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent className="h-8 w-8" style={{color: '#2563EB'}} /> : null;
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <div className="network-header">
        <div className="nav-wrapper">
          <a href="#" className="network-logo">PulseFit Studio</a>
          <nav className="network-nav">
            <a href="#about" className="network-nav-link">About</a>
            <a href="#programs" className="network-nav-link">Programs</a>
            <a href="#schedule" className="network-nav-link">Schedule</a>
            <a href="#pricing" className="network-nav-link">Pricing</a>
            <a href="#contact" className="network-nav-link">Contact</a>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="hero-background min-h-screen flex items-center justify-center"
        style={{ backgroundImage: `url(${mockData.hero.backgroundImage})` }}
      >
        <div className="hero-overlay absolute inset-0"></div>
        <div className="container relative z-10 text-center pad-giant">
          <h1 className="display-large text-white-force mb-6">{mockData.hero.headline}</h1>
          <p className="body-large text-white-force mb-8 max-w-2xl mx-auto">{mockData.hero.subheader}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="btn-cta btn-contrast"
              onClick={() => document.getElementById('booking-section').scrollIntoView({ behavior: 'smooth' })}
            >
              {mockData.hero.primaryCTA}
            </Button>
            <Button 
              className="btn-secondary"
              style={{
                background: 'transparent',
                color: 'white',
                border: '2px solid white',
                fontWeight: '600'
              }}
              onClick={() => document.getElementById('schedule').scrollIntoView({ behavior: 'smooth' })}
            >
              {mockData.hero.secondaryCTA}
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="pad-giant" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={mockData.about.image} 
                alt="Fitness Training"
                className="rounded-3xl hover-lift"
                style={{ width: '100%', height: '400px', objectFit: 'cover' }}
              />
            </div>
            <div>
              <h2 className="heading-1 mb-6">{mockData.about.headline}</h2>
              <p className="body-large">{mockData.about.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="pad-giant" style={{ background: 'var(--bg-subtle)' }}>
        <div className="container">
          <h2 className="heading-1 text-center mb-12">Our Programs</h2>
          <div className="network-grid">
            {mockData.programs.map((program) => (
              <Card key={program.id} className="network-card">
                <CardHeader className="text-center">
                  <div className="mb-4 flex justify-center">
                    {getIcon(program.icon)}
                  </div>
                  <img 
                    src={program.image} 
                    alt={program.title}
                    className="rounded-2xl mb-4"
                    style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                  />
                  <CardTitle className="heading-3">{program.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="body-medium text-center">{program.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section id="schedule" className="pad-giant" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-1 text-center mb-8">Class Schedule</h2>
          
          <Tabs defaultValue="Monday" className="max-w-4xl mx-auto">
            <TabsList className="grid w-full grid-cols-7 mb-8">
              {mockData.schedule.days.map((day) => (
                <TabsTrigger key={day} value={day} className="text-xs">{day.slice(0,3)}</TabsTrigger>
              ))}
            </TabsList>
            
            <div className="flex justify-center mb-6 gap-2 flex-wrap">
              {mockData.schedule.filters.map((filter) => (
                <Button
                  key={filter}
                  variant={selectedFilter === filter ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedFilter(filter)}
                  className={selectedFilter === filter ? "btn-primary" : "btn-secondary"}
                >
                  {filter}
                </Button>
              ))}
            </div>

            <TabsContent value="Monday">
              <div className="grid gap-4">
                {filteredClasses.map((cls, idx) => (
                  <Card key={idx} className="network-card">
                    <CardContent className="flex justify-between items-center p-6">
                      <div>
                        <h4 className="heading-3">{cls.name}</h4>
                        <p className="body-medium text-muted-foreground">with {cls.trainer}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <Badge variant="secondary" style={{color: '#2563EB'}}>{cls.type}</Badge>
                          <span className="flex items-center gap-1 text-sm">
                            <Clock className="h-4 w-4" />
                            {cls.time}
                          </span>
                        </div>
                      </div>
                      <Button className="btn-primary btn-contrast">Book Class</Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
            {/* Add other days similarly */}
          </Tabs>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pad-giant" style={{ background: 'var(--bg-subtle)' }}>
        <div className="container">
          <h2 className="heading-1 text-center mb-12">Choose Your Plan</h2>
          <div className="network-grid">
            {mockData.pricing.map((plan) => (
              <Card key={plan.id} className={`network-card ${plan.popular ? 'border-2 border-[#D3FF62]' : ''}`}>
                <CardHeader className="text-center">
                  {plan.popular && (
                    <Badge className="mb-2 self-center" style={{background: '#D3FF62', color: '#004534'}}>
                      Most Popular
                    </Badge>
                  )}
                  <CardTitle className="heading-2">{plan.name}</CardTitle>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-3xl font-bold">€{plan.price}</span>
                    <span className="text-muted-foreground">/{plan.period}</span>
                  </div>
                  <CardDescription className="body-medium">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full" style={{background: '#D3FF62'}}></div>
                        <span className="body-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    className="w-full btn-primary btn-contrast"
                    onClick={() => handlePlanSelection(plan)}
                  >
                    Choose {plan.name}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="pad-giant" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-1 text-center mb-12">What Our Members Say</h2>
          
          <Carousel className="max-w-4xl mx-auto">
            <CarouselContent>
              {mockData.testimonials.map((testimonial) => (
                <CarouselItem key={testimonial.id}>
                  <Card className="network-card text-center">
                    <CardContent className="p-8">
                      <div className="flex justify-center mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-current" style={{color: '#F87171'}} />
                        ))}
                      </div>
                      <blockquote className="body-large italic mb-6">"{testimonial.text}"</blockquote>
                      <div className="flex items-center justify-center gap-4">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <cite className="heading-3 not-italic">– {testimonial.name}</cite>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking-section" className="pad-giant" style={{ background: 'var(--bg-subtle)' }}>
        <div className="container max-w-2xl mx-auto">
          <h2 className="heading-1 text-center mb-8">Book Your Free Class</h2>
          <Card className="network-card">
            <CardContent className="p-8">
              <form onSubmit={handleBooking} className="space-y-6">
                <div>
                  <Input 
                    placeholder="Your Name"
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Input 
                    type="email"
                    placeholder="Your Email"
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Input 
                    placeholder="Phone Number"
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Input 
                    placeholder="Preferred Time (e.g., Monday 7:00 AM)"
                    value={bookingForm.preferredTime}
                    onChange={(e) => setBookingForm({...bookingForm, preferredTime: e.target.value})}
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-cta btn-contrast"
                  disabled={isBookingLoading}
                >
                  {isBookingLoading ? 'Booking...' : 'Book Free Class'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA Section */}
      <section 
        className="hero-background min-h-[60vh] flex items-center justify-center"
        style={{ backgroundImage: `url(${mockData.finalCTA.backgroundImage})` }}
      >
        <div className="hero-overlay absolute inset-0"></div>
        <div className="container relative z-10 text-center">
          <h2 className="display-medium text-white-force mb-8">{mockData.finalCTA.headline}</h2>
          <Button 
            className="btn-cta btn-contrast"
            onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
          >
            {mockData.finalCTA.ctaText}
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="pad-giant" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="heading-1 mb-6">Get In Touch</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5" style={{color: '#2563EB'}} />
                  <span className="body-medium">{mockData.footer.contact.address}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5" style={{color: '#2563EB'}} />
                  <span className="body-medium">{mockData.footer.contact.phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5" style={{color: '#2563EB'}} />
                  <span className="body-medium">{mockData.footer.contact.email}</span>
                </div>
              </div>
            </div>
            <Card className="network-card">
              <CardContent className="p-6">
                <form onSubmit={handleContact} className="space-y-4">
                  <Input 
                    placeholder="Your Name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    required
                  />
                  <Input 
                    type="email"
                    placeholder="Your Email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    required
                  />
                  <Textarea 
                    placeholder="Your Message"
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                  />
                  <Button 
                    type="submit" 
                    className="w-full btn-primary btn-contrast"
                    disabled={isContactLoading}
                  >
                    {isContactLoading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pad-giant text-white" style={{ background: 'var(--brand-dark)' }}>
        <div className="container">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="heading-3 mb-4 text-white">{mockData.footer.logo}</h3>
              <p className="body-medium text-gray-300">{mockData.footer.tagline}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-white">Classes</h4>
              <ul className="space-y-2">
                {mockData.footer.links.classes.map((link, idx) => (
                  <li key={idx}>
                    <a href="#" className="text-gray-300 hover:text-white transition-colors">{link}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-white">Studio</h4>
              <ul className="space-y-2">
                {mockData.footer.links.studio.map((link, idx) => (
                  <li key={idx}>
                    <a href="#" className="text-gray-300 hover:text-white transition-colors">{link}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-white">Support</h4>
              <ul className="space-y-2">
                {mockData.footer.links.support.map((link, idx) => (
                  <li key={idx}>
                    <a href="#" className="text-gray-300 hover:text-white transition-colors">{link}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 mb-4 md:mb-0">© 2024 PulseFit Studio. All rights reserved.</p>
            <div className="flex gap-4">
              <Facebook className="h-5 w-5 text-gray-400 hover:text-white cursor-pointer transition-colors" />
              <Instagram className="h-5 w-5 text-gray-400 hover:text-white cursor-pointer transition-colors" />
              <Twitter className="h-5 w-5 text-gray-400 hover:text-white cursor-pointer transition-colors" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PulseFitStudio;